import React, { useState } from 'react';
import { Sidebar, Menu, MenuItem } from 'react-pro-sidebar';
import { Link, useLocation } from 'react-router-dom';
import { Box, Typography, IconButton, Avatar, Chip } from '@mui/material';
import GroupIcon from '@mui/icons-material/Group';
import SubscriptionsIcon from '@mui/icons-material/Subscriptions';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import LogoutIcon from '@mui/icons-material/Logout';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { adminLogout } from '../../store/slices/adminAuthSlice';
import { NAVIGATION_ROUTE } from '../../constants';
import { useSelector } from 'react-redux';
import { selectAdminAuth } from '../../store/slices/adminAuthSlice';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';

const sidebarLinks = [
  { label: 'User Management', path: NAVIGATION_ROUTE.adminUserManagement, icon: <GroupIcon /> },
  { label: 'Plans Management', path: NAVIGATION_ROUTE.adminPlans, icon: <SubscriptionsIcon /> },
  { label: 'Transaction History', path: NAVIGATION_ROUTE.adminTransactions, icon: <ReceiptLongIcon /> },
];

function getInitials(firstName, lastName) {
  if (!firstName && !lastName) return 'A';
  return `${firstName?.[0] || ''}${lastName?.[0] || ''}`.toUpperCase();
}

const AdminSidebar = ({ collapsed, setCollapsed }) => {
  const location = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { admin } = useSelector(selectAdminAuth);
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);
  const [logoutLoading, setLogoutLoading] = useState(false);

  const handleLogout = async () => {
    setLogoutDialogOpen(true);
  };
  const handleLogoutConfirm = async () => {
    setLogoutLoading(true);
    setTimeout(async () => {
      setLogoutDialogOpen(false);
      const resultAction = await dispatch(adminLogout());
      setLogoutLoading(false);
      if (adminLogout.fulfilled.match(resultAction)) {
        navigate('/');
      }
    }, 700);
  };
  const handleLogoutCancel = () => {
    setLogoutDialogOpen(false);
  };

  const handleAdminClick = () => {
    navigate('/admin/profile');
  };

  const initials = getInitials(admin?.firstName, admin?.lastName);
  // const fullName = (admin?.firstName || admin?.lastName) ? `${admin?.firstName || ''} ${admin?.lastName || ''}`.trim() : 'Admin';
  const email = admin?.email || '';

  return (
    <Sidebar
      collapsed={collapsed}
      style={{
        height: '100vh',
        borderRight: '1px solid #e0e0e0',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        minWidth: collapsed ? 72 : 240,
        transition: 'min-width 0.2s',
        overflow: 'visible',
      }}
      backgroundColor="rgba(255, 255, 255, 0.95)"
    >
      <div>
        <Box
          sx={{
            p: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            minWidth: collapsed ? 56 : 200,
            transition: 'min-width 0.2s',
          }}
        >
          <Box
            sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', width: 'auto' }}
            onClick={handleAdminClick}
          >
            <Avatar
              sx={{
                width: collapsed ? 40 : 56,
                height: collapsed ? 40 : 56,
                fontWeight: 700,
                fontSize: collapsed ? 20 : 28,
                background: 'linear-gradient(135deg, #1976d2 30%, #42a5f5 90%)',
                color: '#fff',
                boxShadow: 3,
                border: '3px solid #fff',
                transition: 'all 0.2s',
              }}
            >
              {initials}
            </Avatar>
            {!collapsed && (
              <Box sx={{ ml: 2, display: 'flex', flexDirection: 'column', alignItems: 'flex-start', minWidth: 0 }}>
                <Typography variant="subtitle1" fontWeight={700} sx={{ color: '#1976d2', lineHeight: 1.2, maxWidth: 140, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {/* {fullName} */}
                  OFFENDERLIST
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ maxWidth: 140, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {email}
                </Typography>
                <Chip label="Admin" size="small" color="primary" sx={{ mt: 0.5, fontWeight: 600, fontSize: 11, letterSpacing: 0.5, borderRadius: 1 }} />
              </Box>
            )}
          </Box>
          <IconButton
            size="medium"
            onClick={() => setCollapsed((prev) => !prev)}
            sx={{
              ml: 1,
              borderRadius: '50%',
              background: '#f5f7fa',
              color: '#1976d2',
              boxShadow: 1,
              transition: 'background 0.18s, color 0.18s',
              p: 0.5,
              minWidth: 40,
              minHeight: 40,
              '&:hover': {
                background: 'rgba(25, 118, 210, 0.12)',
                color: '#1565c0',
              },
            }}
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {collapsed ? <ChevronRightIcon sx={{ fontSize: 28 }} /> : <ChevronLeftIcon sx={{ fontSize: 28 }} />}
          </IconButton>
        </Box>
        <Menu
          menuItemStyles={{
            button: ({ active }) => ({
              color: active ? '#1976d2' : '#333',
              backgroundColor: active ? '#e3f2fd' : 'transparent',
              '&:hover': {
                backgroundColor: '#f5f5f5 !important',
                color: '#1976d2 !important',
              },
            }),
          }}
        >
          {sidebarLinks.map((link) => (
            <MenuItem
              key={link.label}
              active={location.pathname === link.path}
              icon={link.icon}
              component={<Link to={link.path} />}
            >
              {link.label}
            </MenuItem>
          ))}
          <MenuItem
            key="Logout"
            icon={<LogoutIcon />}
            onClick={handleLogout}
            style={{ marginTop: 24, color: '#d32f2f', fontWeight: 600 }}
          >
            Logout
          </MenuItem>
        </Menu>
      </div>
      <Dialog open={logoutDialogOpen} onClose={logoutLoading ? undefined : handleLogoutCancel} maxWidth="xs" fullWidth>
        <DialogTitle>Confirm Logout</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to log out?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleLogoutCancel} color="primary" sx={{ borderRadius: 1, fontSize: 14 }} disabled={logoutLoading}>Cancel</Button>
          <Button onClick={handleLogoutConfirm} color="error" variant="contained" sx={{ borderRadius: 1, fontSize: 14 }} disabled={logoutLoading}>
            {logoutLoading ? <CircularProgress size={20} color="inherit" /> : 'Logout'}
          </Button>
        </DialogActions>
      </Dialog>
    </Sidebar>
  );
};

export default AdminSidebar; 